/**
 * @fileoverview Utility functions for generating file previews.
 * Supports images, PDFs, Excel spreadsheets, Word documents, and text files.
 */

// ============================================
// Constants
// ============================================

/** Maximum number of rows to display in Excel preview for performance */
export const MAX_EXCEL_PREVIEW_ROWS = 500;

/** Maximum number of columns to display in Excel preview for performance */
export const MAX_EXCEL_PREVIEW_COLS = 50;

/** Maximum number of sheets to process in Excel preview */
export const MAX_EXCEL_SHEETS = 10;

/** Supported image file extensions */
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'] as const;

/** Supported Excel file extensions */
export const EXCEL_EXTENSIONS = ['xlsx', 'xls'] as const;

/** Supported Word file extensions */
export const WORD_EXTENSIONS = ['doc', 'docx'] as const;

/** Supported text file extensions */
export const TEXT_EXTENSIONS = ['txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'ts'] as const;

// ============================================
// Types
// ============================================

/** Supported preview types */
export type PreviewType = 'text' | 'image' | 'pdf' | 'excel' | 'word' | 'unsupported';

/** Cell value type for Excel data */
export type ExcelCellValue = string | number | boolean | null;

/** Data structure for a single Excel sheet */
export interface ExcelSheetData {
  /** Name of the sheet */
  name: string;
  /** 2D array of cell values */
  data: ExcelCellValue[][];
  /** Total number of rows in the original sheet */
  totalRows: number;
  /** Total number of columns in the original sheet */
  totalCols: number;
}

/** Structured data for Excel file preview */
export interface ExcelPreviewData {
  /** Array of sheet data */
  sheets: ExcelSheetData[];
  /** Index of the initially active sheet */
  activeSheet: number;
}

/** Structured data for Word document preview */
export interface WordPreviewData {
  /** HTML representation of the document */
  html: string;
  /** Plain text representation of the document */
  text: string;
  /** Conversion warnings/messages from mammoth.js */
  messages: string[];
}

/** Union type for all possible preview data types */
export type PreviewData = string | ArrayBuffer | ExcelPreviewData | WordPreviewData | null;

/** Result of a preview generation operation */
export interface PreviewResult {
  /** Type of preview content */
  type: PreviewType;
  /** The preview data (varies by type) */
  data: PreviewData;
  /** Object URL for file types that need it (images, PDFs) */
  objectUrl?: string;
  /** Error message if preview generation failed */
  error?: string;
}

// ============================================
// Type Guards
// ============================================

/**
 * Type guard to check if data is ExcelPreviewData
 */
export function isExcelPreviewData(data: PreviewData): data is ExcelPreviewData {
  return (
    data !== null &&
    typeof data === 'object' &&
    'sheets' in data &&
    Array.isArray((data as ExcelPreviewData).sheets)
  );
}

/**
 * Type guard to check if data is WordPreviewData
 */
export function isWordPreviewData(data: PreviewData): data is WordPreviewData {
  return (
    data !== null &&
    typeof data === 'object' &&
    'html' in data &&
    'text' in data &&
    typeof (data as WordPreviewData).html === 'string'
  );
}

// ============================================
// Utility Functions
// ============================================

/**
 * Gets the file extension from a filename
 */
function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

// ============================================
// Preview Type Detection
// ============================================

/**
 * Determines the preview type based on file extension and MIME type.
 * @param file - The file to analyze
 * @returns The determined preview type
 */
export function getPreviewType(file: File): PreviewType {
  const extension = getFileExtension(file.name);
  const mimeType = file.type.toLowerCase();

  // Images
  if (
    mimeType.startsWith('image/') ||
    IMAGE_EXTENSIONS.includes(extension as (typeof IMAGE_EXTENSIONS)[number])
  ) {
    return 'image';
  }

  // PDF
  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf';
  }

  // Excel
  if (
    mimeType.includes('excel') ||
    mimeType.includes('spreadsheet') ||
    EXCEL_EXTENSIONS.includes(extension as (typeof EXCEL_EXTENSIONS)[number])
  ) {
    return 'excel';
  }

  // Word
  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    WORD_EXTENSIONS.includes(extension as (typeof WORD_EXTENSIONS)[number])
  ) {
    return 'word';
  }

  // Text-based files
  if (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    TEXT_EXTENSIONS.includes(extension as (typeof TEXT_EXTENSIONS)[number])
  ) {
    return 'text';
  }

  return 'unsupported';
}

/**
 * Generate preview for image files
 */
export async function generateImagePreview(file: File): Promise<PreviewResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (result) {
        const objectUrl = URL.createObjectURL(file);
        resolve({
          type: 'image',
          data: result as string,
          objectUrl,
        });
      } else {
        reject(new Error('Failed to read image file'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Generate preview for PDF files
 */
export async function generatePDFPreview(file: File): Promise<PreviewResult> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    resolve({
      type: 'pdf',
      data: null,
      objectUrl,
    });
  });
}

// ============================================
// Excel Preview Generation
// ============================================

/**
 * Creates an error result for Excel preview
 */
function createExcelErrorResult(message: string, errorType: string): PreviewResult {
  return {
    type: 'excel',
    data: message,
    error: errorType,
  };
}

/**
 * Normalizes Excel row data to ensure consistent column count
 */
function normalizeExcelRows(rows: ExcelCellValue[][], maxCols: number): ExcelCellValue[][] {
  return rows.map((row) => {
    const normalizedRow = [...row];
    while (normalizedRow.length < maxCols) {
      normalizedRow.push(null);
    }
    return normalizedRow;
  });
}

/**
 * Generates a preview for Excel files using SheetJS.
 * Returns structured data for spreadsheet rendering.
 *
 * @param file - The Excel file to preview
 * @returns Promise resolving to PreviewResult with ExcelPreviewData
 */
export async function generateExcelPreview(file: File): Promise<PreviewResult> {
  try {
    const XLSX = await import('xlsx');

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            resolve(createExcelErrorResult('Failed to read Excel file', 'File read error'));
            return;
          }

          const workbook = XLSX.read(data, { type: 'binary' });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            resolve(
              createExcelErrorResult('Excel file appears to be empty or invalid', 'Empty workbook'),
            );
            return;
          }

          // Process sheets (limited for performance)
          const sheets: ExcelSheetData[] = workbook.SheetNames.slice(0, MAX_EXCEL_SHEETS).map(
            (sheetName) => {
              const worksheet = workbook.Sheets[sheetName];
              const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
              const totalRows = range.e.r - range.s.r + 1;
              const totalCols = range.e.c - range.s.c + 1;

              // Convert to array format
              const jsonData = XLSX.utils.sheet_to_json<ExcelCellValue[]>(worksheet, {
                header: 1,
                defval: null,
                blankrows: false,
              });

              // Apply row and column limits
              const limitedData = jsonData.slice(0, MAX_EXCEL_PREVIEW_ROWS).map((row) => {
                const rowArray = Array.isArray(row) ? row : [row];
                return rowArray.slice(0, MAX_EXCEL_PREVIEW_COLS);
              });

              // Normalize column count
              const maxCols = Math.min(
                MAX_EXCEL_PREVIEW_COLS,
                Math.max(...limitedData.map((row) => row.length), 1),
              );

              return {
                name: sheetName,
                data: normalizeExcelRows(limitedData, maxCols),
                totalRows,
                totalCols,
              };
            },
          );

          resolve({
            type: 'excel',
            data: { sheets, activeSheet: 0 },
          });
        } catch {
          resolve(
            createExcelErrorResult(
              'Failed to parse Excel file. The file may be corrupted or in an unsupported format.',
              'Parse error',
            ),
          );
        }
      };

      reader.onerror = () => {
        resolve(createExcelErrorResult('Error reading Excel file', 'File read error'));
      };

      reader.readAsBinaryString(file);
    });
  } catch {
    return createExcelErrorResult(
      'Excel preview requires the xlsx library. Install xlsx package for full preview support.',
      'xlsx library not available',
    );
  }
}

// ============================================
// Word Preview Generation
// ============================================

/** Mammoth.js style mapping for Word document conversion */
const MAMMOTH_STYLE_MAP = [
  "p[style-name='Heading 1'] => h1:fresh",
  "p[style-name='Heading 2'] => h2:fresh",
  "p[style-name='Heading 3'] => h3:fresh",
  "p[style-name='Title'] => h1.document-title:fresh",
  'b => strong',
  'i => em',
  'u => u',
  'strike => del',
];

/**
 * Creates an error result for Word preview
 */
function createWordErrorResult(message: string, errorType: string): PreviewResult {
  return {
    type: 'word',
    data: message,
    error: errorType,
  };
}

/**
 * Converts plain text to basic HTML paragraphs
 */
function textToHtml(text: string): string {
  return `<p>${text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

/**
 * Generates a preview for Word documents with HTML formatting.
 * Uses mammoth.js for .docx files, returns error for .doc files.
 *
 * @param file - The Word document to preview
 * @returns Promise resolving to PreviewResult with WordPreviewData
 */
export async function generateWordPreview(file: File): Promise<PreviewResult> {
  const extension = getFileExtension(file.name);

  // Only .docx is supported
  if (extension !== 'docx') {
    return createWordErrorResult(
      'Preview for .doc files is not supported. Please convert to .docx or upload the file to view its contents.',
      '.doc format not supported',
    );
  }

  try {
    const mammoth = await import('mammoth');

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;

          // Convert to HTML for rich preview
          const htmlResult = await mammoth.convertToHtml(
            { arrayBuffer },
            { styleMap: MAMMOTH_STYLE_MAP },
          );

          // Also get plain text for fallback/alternative view
          const textResult = await mammoth.extractRawText({ arrayBuffer });

          const hasHtml = htmlResult.value && htmlResult.value.trim().length > 0;
          const hasText = textResult.value && textResult.value.trim().length > 0;

          if (hasHtml) {
            resolve({
              type: 'word',
              data: {
                html: htmlResult.value,
                text: textResult.value,
                messages: htmlResult.messages.map((m) => m.message),
              },
            });
          } else if (hasText) {
            // Fallback: convert plain text to HTML
            resolve({
              type: 'word',
              data: {
                html: textToHtml(textResult.value),
                text: textResult.value,
                messages: [],
              },
            });
          } else {
            resolve(
              createWordErrorResult(
                'Word document appears to be empty or contains only images/formatting.',
                'Empty document',
              ),
            );
          }
        } catch {
          resolve(
            createWordErrorResult(
              'Failed to extract content from Word document. The file may be corrupted or password-protected.',
              'Word parsing failed',
            ),
          );
        }
      };

      reader.onerror = () => {
        resolve(createWordErrorResult('Error reading Word document.', 'File read error'));
      };

      reader.readAsArrayBuffer(file);
    });
  } catch {
    return createWordErrorResult(
      'Word preview requires the mammoth library for .docx files. Install mammoth package for full preview support.',
      'mammoth library not available',
    );
  }
}

/**
 * Generate preview for text-based files
 */
export async function generateTextPreview(file: File): Promise<PreviewResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve({
          type: 'text',
          data: result,
          objectUrl: undefined,
        });
      } else {
        reject(new Error('Failed to read text file'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading text file'));
    reader.readAsText(file);
  });
}

/**
 * Generate preview for any file type
 */
export async function generatePreview(file: File): Promise<PreviewResult> {
  const previewType = getPreviewType(file);

  switch (previewType) {
    case 'image':
      return generateImagePreview(file);
    case 'pdf':
      return generatePDFPreview(file);
    case 'excel':
      return generateExcelPreview(file);
    case 'word':
      return generateWordPreview(file);
    case 'text':
      return generateTextPreview(file);
    default:
      return Promise.resolve({
        type: 'unsupported',
        data: 'Preview is not available for this file type. Please upload the file to view its contents.',
        error: 'Unsupported file type',
      });
  }
}

/**
 * Clean up object URLs to prevent memory leaks
 */
export function revokeObjectUrl(objectUrl: string): void {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
  }
}
